// src/lib/firebase.ts
import {
  initializeApp,
  getApps,
  getApp,
  type FirebaseApp,
} from "@firebase/app";
import { connectFunctionsEmulator, getFunctions } from "@firebase/functions";
import { connectAuthEmulator, initializeAuth, indexedDBLocalPersistence, browserLocalPersistence } from "@firebase/auth";
import {
  connectFirestoreEmulator,
  initializeFirestore,
  memoryLocalCache,
} from "@firebase/firestore";
import { connectStorageEmulator, getStorage } from "@firebase/storage";
import { Capacitor } from '@capacitor/core';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Debug: Log if Firebase config is missing (helps debug iOS issues)
if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
  console.error('Firebase config missing! Check environment variables:', {
    hasApiKey: !!firebaseConfig.apiKey,
    hasProjectId: !!firebaseConfig.projectId,
  });
} else {
  console.log('✅ Firebase config loaded:', {
    projectId: firebaseConfig.projectId,
    authDomain: firebaseConfig.authDomain,
  });
}

// Singleton Pattern for Firebase App Initialization
function getFirebaseApp(): FirebaseApp {
  console.log('🔥 Initializing Firebase app...');
  if (getApps().length > 0) {
    console.log('✅ Firebase app already initialized');
    return getApp();
  }
  console.log('🔥 Creating new Firebase app instance');
  const app = initializeApp(firebaseConfig);
  console.log('✅ Firebase app created successfully');
  return app;
}

const app = getFirebaseApp();

// IndexedDB can stop responding (in the iOS/Android WebView after the app has
// been in the background, and in browsers with several tabs fighting over the
// cache). Everything waiting on it then hangs: screens that load forever until
// the app is restarted, and a retry button that does nothing. So:
// - Firestore keeps its cache in memory only (on every platform).
// - On native, Auth keeps the session in localStorage. A session stored in
//   IndexedDB by older versions is found and moved over automatically.
const isNative = Capacitor.isNativePlatform();
// IndexedDB is only listed to move a session saved there by an older version.
// Once the session is in localStorage, opening IndexedDB just slowed the start
// (~0.7s on iPhone), so it's skipped.
function sessionInLocalStorage(): boolean {
  try {
    const key = `firebase:authUser:${firebaseConfig.apiKey}:[DEFAULT]`;
    return localStorage.getItem(key) !== null;
  } catch {
    return false;
  }
}
const auth = initializeAuth(app, {
  persistence: isNative && !sessionInLocalStorage()
    ? [browserLocalPersistence, indexedDBLocalPersistence]
    : browserLocalPersistence
});
const db = initializeFirestore(app, {
  // Local emulator testing only: the iOS simulator gets no live updates from
  // the emulator over a streamed connection, so poll instead.
  ...(import.meta.env.VITE_USE_EMULATORS === "true" && isNative
    ? { experimentalForceLongPolling: true }
    : { experimentalAutoDetectLongPolling: true }),
  localCache: memoryLocalCache(),
});

// Free the space used by the Firestore cache of older app versions, once.
// Only in the apps: in a browser, a tab still running an older version uses
// that database, and deleting it would stop Firestore in that tab.
const OLD_CACHE_CLEARED_KEY = "nn-old-firestore-cache-cleared";
if (isNative && firebaseConfig.projectId && typeof indexedDB !== "undefined") {
  try {
    if (!localStorage.getItem(OLD_CACHE_CLEARED_KEY)) {
      indexedDB.deleteDatabase(`firestore/[DEFAULT]/${firebaseConfig.projectId}/main`);
      localStorage.setItem(OLD_CACHE_CLEARED_KEY, "1");
    }
  } catch {
    // Not important.
  }
}
const storage = getStorage(app);
console.log('✅ Storage initialized');
console.log('🔥 Getting Functions...');
const functions = getFunctions(app);
console.log('✅ Functions initialized');

// Local testing against the Firebase emulators (never set in real builds):
// VITE_USE_EMULATORS=true with a demo-* project id.
if (import.meta.env.VITE_USE_EMULATORS === "true") {
  connectAuthEmulator(auth, "http://127.0.0.1:9099", { disableWarnings: true });
  connectFirestoreEmulator(db, "127.0.0.1", Number(import.meta.env.VITE_FIRESTORE_EMULATOR_PORT || 8080));
  connectFunctionsEmulator(functions, "127.0.0.1", 5001);
  connectStorageEmulator(storage, "127.0.0.1", 9199);
}

export { app, auth, db, storage, functions };
